import { createContext, useContext, ReactNode } from "react";
import enTranslations from "../locales/en.json";
import elTranslations from "../locales/el.json";

type Translations = typeof enTranslations;

interface I18nContextType {
  locale: string;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const translations: Record<string, Translations> = {
  en: enTranslations,
  el: elTranslations,
};

export function I18nProvider({
  children,
  locale,
}: {
  children: ReactNode;
  locale: string;
}) {
  const t = (key: string, params?: Record<string, string | number>): string => {
    // Navigate through nested keys (e.g., "submissions.title")
    const keys = key.split(".");
    let value: any = translations[locale] || translations.el;

    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        console.warn(`Translation key not found: ${key}`);
        return key;
      }
    }

    if (typeof value !== "string") {
      console.warn(`Translation value is not a string: ${key}`);
      return key;
    }

    // Replace parameters in the string (e.g., {count}, {date})
    if (params) {
      return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey]?.toString() ?? match;
      });
    }

    return value;
  };

  return (
    <I18nContext.Provider value={{ locale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useTranslation must be used within I18nProvider");
  }
  return context;
}
